import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { useSessionQuery, SessionContext } from '@/hooks/useSession';
import { ApiError } from '@/lib/api';
import { TabBar } from '@m/ui/TabBar';
import { Button, Skeleton } from '@m/ui/Controls';
import { SignInScreen } from '@m/screens/SignIn';
import { TimeScreen } from '@m/screens/Time';
import { InvoicesScreen } from '@m/screens/Invoices';
import { InvoiceDetailScreen } from '@m/screens/InvoiceDetail';
import { ClientsScreen } from '@m/screens/Clients';
import { ClientDetailScreen } from '@m/screens/ClientDetail';
import { MoreScreen } from '@m/screens/More';
import { ReportsScreen } from '@m/screens/more/Reports';
import { CreditsFeesScreen } from '@m/screens/more/CreditsFees';
import { PersonalHoursScreen } from '@m/screens/more/PersonalHours';
import { TeamScreen } from '@m/screens/more/Team';
import { SubscriptionScreen } from '@m/screens/more/Subscription';
import { InvoiceSettingsScreen } from '@m/screens/more/InvoiceSettings';
import { SettingsScreen } from '@m/screens/more/Settings';
import { AssistantScreen } from '@m/screens/more/Assistant';

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInScreen />} />
      <Route element={<AuthGate />}>
        <Route index element={<Navigate to="/time" replace />} />
        <Route path="/time" element={<TimeScreen />} />
        <Route path="/invoices" element={<InvoicesScreen />} />
        <Route path="/invoices/:id" element={<InvoiceDetailScreen />} />
        <Route path="/clients" element={<ClientsScreen />} />
        <Route path="/clients/:id" element={<ClientDetailScreen />} />
        <Route path="/more" element={<MoreScreen />} />
        <Route path="/more/reports" element={<ReportsScreen />} />
        <Route path="/more/credits" element={<CreditsFeesScreen />} />
        <Route path="/more/personal-hours" element={<PersonalHoursScreen />} />
        <Route path="/more/team" element={<TeamScreen />} />
        <Route path="/more/subscription" element={<SubscriptionScreen />} />
        <Route path="/more/invoice-settings" element={<InvoiceSettingsScreen />} />
        <Route path="/more/settings" element={<SettingsScreen />} />
        <Route path="/more/assistant" element={<AssistantScreen />} />
        {/* Desktop paths a shared link or the shell may still open. */}
        <Route path="/timesheet" element={<Navigate to="/time" replace />} />
        <Route path="*" element={<Navigate to="/time" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * Signed out goes to /sign-in, which the iOS shell intercepts and turns into
 * its native Google sheet; in a plain browser it is the sign-in screen.
 */
function AuthGate() {
  const location = useLocation();
  const session = useSessionQuery();

  if (session.error instanceof ApiError && session.error.isAuth) {
    const redirect = location.pathname + location.search;
    return <Navigate to={`/sign-in?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  if (session.error && !session.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
        <div className="text-title font-semibold">Can’t reach Billable</div>
        <p className="text-secondary text-[var(--text-muted)]">Check your connection and try again.</p>
        <Button variant="secondary" block={false} onClick={() => void session.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (!session.data) {
    return (
      <div className="px-4 pt-[calc(var(--sat)+56px)]">
        <Skeleton className="mb-6 h-9 w-40" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  return (
    <SessionContext.Provider value={session.data}>
      {/* Screen above, tab bar below, in normal flow: nothing is fixed over
          the content, so nothing needs padding to clear it. */}
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
        <TabBar />
      </div>
    </SessionContext.Provider>
  );
}
